import React, { useEffect, useState } from 'react';
import at from 'lodash/at';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import Loader from '../../cruxComponents/Loader';
import { getCSSVariables, getThemeFromConfig } from '../../cruxComponents/utils';
import Left from './Left';
import Right from './Right';
import NeedHelpPopupDialog from '../../Components/UIElements/NeedHelpPopupDialog';
import { useDocumentWidth } from '../../utils';
import Cover from '../../Components/StyledComponents/MaskCover/Index';

const preventKeyboard = (e) => {
  if ([9, 16, 13, 32, 37, 38, 39, 40].includes(e.keyCode)) {
    /* Arrow Up, down, left, right
			Shift, Tab, Enter, space
		*/
    e.preventDefault();
  }
};

const ContentWrapper = styled.div`
  ${({ customHeader: { isPresent }, headerPosition, showSubHeader }) =>
    headerPosition === 'fixed' &&
    isPresent &&
    `
    margin-top: ${showSubHeader ? '155px' : '128px'};
    @media (min-width: 1151px) {
      margin-top: ${showSubHeader ? '135px' : '128px'};
    }
  `};
  ${({ background, layout, pageId, isLoading }) => {
    if (layout === 'single' && pageId === 'review') {
      return !isLoading
        ? 'background: linear-gradient(rgb(245, 245, 245) 480px, rgb(247, 247, 247) 480px)'
        : 'background: #ffffff';
    }
    if (layout === 'single') {
      return `background: linear-gradient(180deg, ${background} 480px, #f7f7f7 480px);`;
    }
    if (layout === 'auto') {
      return 'background: #ffffff';
    }
    return `background: ${background}`;
  }};
  flex: 1;
  ${({ isLoading, pageId }) => !isLoading && pageId !== 'notfound' && 'display: flex'};
  ${({ pageId }) =>
    (pageId === 'notfound' || pageId === 'login' || pageId === 'knockout') &&
    `
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    `}
  ${({ isLoading, skipPageLoader }) =>
    isLoading && !skipPageLoader && 'width: 100%; justify-content: center;'};
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: center;
  ${({ pageId }) => pageId === 'inactivesession' && 'align-items: center'};
  flex-direction: ${({ screenViewport }) => (screenViewport === 'mobile' ? 'column' : 'row')};
  flex: 1 1 0;
  width: 100%;
`;

const Content = ({
  children,
  layout,
  loading,
  theme,
  leftData,
  reload,
  role,
  showSubHeader,
  lastFocusedElementId,
}) => {
  const {
    screenViewport,
    activeSubsectionId,
    config: {
      theme: {
        global: { background, leftSidebarColor, headerTheme },
      },
      customHeader,
      global,
      needHelpPopupDialog,
    },
  } = theme;
  const { pageId, config } = theme;
  const { background: pageBackground } = get(config, `theme.pageOverrides.${pageId}`, background);
  const [backgroundSize, setbackgroundSize] = useState();
  const focusRef = React.useRef();
  const docWidth = useDocumentWidth();
  const configHeight = 800;
  const configWidth = 1440;

  const { headerPosition } = getThemeFromConfig(theme, 'globalHeaderTheme');
  const position = get(headerPosition, theme.screenViewport);

  const buttonIds = ['button', 'btn'];
  const buttonIdRegex = new RegExp(`^(${buttonIds.join('|')})`, 'i');
  let isButtonFocus = false;
  if (lastFocusedElementId) {
    isButtonFocus =
      buttonIdRegex.test(lastFocusedElementId) || !!lastFocusedElementId?.match(/button/i);
  }

  useEffect(() => {
    if (loading) {
      document.addEventListener('keydown', preventKeyboard);
    } else {
      document.removeEventListener('keydown', preventKeyboard);
    }
  }, [loading]);

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', preventKeyboard);
    };
  }, []);

  useEffect(() => {
    if (
      focusRef &&
      focusRef.current &&
      focusRef.current.focus &&
      typeof focusRef.current.focus === 'function' &&
      isButtonFocus
    ) {
      focusRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setbackgroundSize(
      docWidth > configWidth
        ? `${docWidth}px ${(docWidth / configWidth) * configHeight}px`
        : headerTheme?.['background-size'],
    );
  }, [docWidth]);

  // CSS Variables
  const cssVariables = getCSSVariables(theme);
  const height = at(cssVariables, [
    'pageOverride.height.content-main',
    'global.height.content-main',
  ]);

  const _headerTheme = {
    ...headerTheme,
    height: height[0] || height[1],
    'background-size': backgroundSize,
  };
  if (layout === 'auto') {
    return (
      <ContentWrapper
        data-name='main'
        leftSidebarColor={ leftSidebarColor }
        layout={ layout }
        background={ pageBackground }
        role={ role }
        headerPosition={ headerPosition }
        customHeader={ customHeader }
        pageId={ pageId }
      >
        {children}
      </ContentWrapper>
    );
  }

  const renderPage = () => {
    const layoutKeys = ['completed', 'timeout', 'inactivesession', 'knockout', 'challenge', 'csr'];
    const activeLayout = layoutKeys.some((key) => window.location.href.includes(key));

    return (
      <FlexWrapper screenViewport={ screenViewport } data-name='main-wrapper' pageId={ pageId }>
        {loading && (
          <Cover ref={ focusRef } aria-label='Processing' tabIndex={ 0 } aria-live='polite' />
        )}
        <Left
          leftSidebarColor={ leftSidebarColor }
          layout={ layout }
          screenViewport={ screenViewport }
          leftData={ leftData }
        />
        <Right
          screenViewport={ screenViewport }
          content={ children }
          layout={ layout }
          leftData={ leftData }
          pageId={ pageId }
        />
        {role !== 'csr' && !activeLayout && children?.props?.questions?.length > 0 && (
          <NeedHelpPopupDialog global={ global } needHelpPopupDialog={ needHelpPopupDialog } />
        )}
      </FlexWrapper>
    );
  };
  const skipPageLoader = (layout === 'double' || activeSubsectionId !== '') && !reload;
  return (
    <ContentWrapper
      data-name='main'
      layout={ layout }
      background={ pageBackground }
      role={ role }
      headerPosition={ position }
      customHeader={ customHeader }
      isLoading={ loading }
      skipPageLoader={ skipPageLoader }
      pageId={ pageId }
      screenViewport={ screenViewport }
      showSubHeader={ showSubHeader }
    >
      {headerTheme && <div style={ _headerTheme } />}
      {loading && !skipPageLoader ? <Loader /> : renderPage()}
    </ContentWrapper>
  );
};

Content.defaultProps = {
  loading: false,
  leftData: [],
  layout: 'single',
};

Content.propTypes = {
  leftData: PropTypes.array,
  layout: PropTypes.string,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
};

export default withTheme(Content);
